import Navbar from "@/Components/Navbar"
import { useNavigate, useParams } from "react-router-dom"
import { resolveBackgroundUrl } from "@/utils/gameBackgrounds"
import { useEffect, useState } from "react"
import { getUserCreds, ORIGINAL_GAME_STORE_LINK, socket } from "@/utils/utils"
import Rank from "@/Components/Rank"
import rankImage from "@/images/rank.webp"
import Board from "./Board"
import DiceArea from "./DiceArea"
import Sidebar from "./Sidebar"
import MoneyAnimation from "@/Components/MoneyAnimation"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData, setGlobalElement } from "@/store/globalSlice"
import CenterNotifications from "@/CenterNotifications"
import BankruptModal from "@/Modals/BankruptModal"
import VoiceChat from "@/Components/VoiceChat"
import { HoveredTooltipItem } from "./HoveredTooltipItem"
import classNames from "classnames"
import GameHistory from "@/Components/GameHistory"
import GlobalChat from "@/Components/GlobalChat"
import SidebarLeft from "./SidebarLeft"
import ThemeSwitcherModal from "@/Modals/ThemeSwitcherModal"
import LeaveGameConfirmModal from "@/Modals/LeaveGameConfirmModal"
import FastPaceNewPlayerConfirmModal from "@/Modals/FastPaceNewPlayerConfirmModal"
import SettingsModal from "@/Modals/SettingsModal"
import HelpModal from "@/Modals/HelpModal"
import GuideModal from "@/Modals/GuideModal"
import AuctionResult from "@/Modals/AuctionResult"
import FullStats from "@/Components/FullStats"
import HiddenAuctionModal from "@/Modals/HiddenAuctionModal"
import HouseBuildModal from "@/Modals/HouseBuildModal"
import DoubleRentModalPage from "@/Modals/DoubleRentModal"
import ForcedBuyModalPage from "@/Modals/ForcedBuyModal"
import EndGameModalPage from "@/Modals/EndGameModal"
import AuctionPage from "@/Pages/Auction"
import TradePlayerChooser from "@/Modals/TradePlayerChooser"
import CreateTradeModal from "@/Modals/CreateTradeModal"
import PropertyManagementPage from "@/Pages/PropertyManagement"
import RolePickingModalPage from "@/Modals/RolePickingModal"
import SayNoModal from "@/Modals/SayNoModal"
import PlayerCardsModal from "@/Modals/PlayerCardsModal"
import BuyOrAuctionModal from "@/Modals/BuyOrAuctionModal"
import JailFreeCardModal from "@/Modals/JailFreeCardModal"
import StockModal from "@/Modals/StockModal"
import DecisionHubModal from "@/Modals/DecisionHubModal"
import PauseOverlay from "@/Components/PauseOverlay"
import { getPlayerOrderFromPlayerId } from "@/utils/animationHelpers"
import { TradeNotification } from "@/CenterNotifications/TradeNotification"
import { featureFlags, isDemoMode } from "@mono/constants"
import GameNotAvailable from "./GameNotAvailable"
import { useIdleAutoAction } from "@/Hooks/useIdleAutoAction"
interface NewGameProps {}

const Game: React.FC<NewGameProps> = () => {
  const params = useParams()
  const id = params.id
  const dispatch = useAppDispatch()
  // Field by field rather than the whole `state.global` slice. This component
  // is the root of the game screen, so a selector returning the slice object
  // re-rendered every modal, overlay and notification layer below on *any*
  // dispatch anywhere in the app — a chat message, a toast, an animation flag.
  // Each of these changes only when the thing it names does.
  const myGame = useAppSelector((state) => state.global.myGame)
  const situation = useAppSelector((state) => state.global.myGame?.situation)
  const activeAuctionPropertyOrder = useAppSelector(
    (state) => state.global.myGame?.situation?.activeAuctionPropertyOrder
  )
  const selectedThemeId = useAppSelector(
    (state) => state.global.selectedTheme?.id
  )
  const modalData = useAppSelector((state) => state.global.modalData)
  const ActiveModal = useAppSelector((state) => state.global.modal)
  const tradeData = useAppSelector((state) => state.global.tradeData)
  const viewedTradeId = useAppSelector((state) => state.global.viewedTradeId)
  const moneyAnimations = useAppSelector(
    (state) => state.global.moneyAnimations
  )
  const nav = useNavigate()

  // Mounted at the game root, above every early return below, so the idle
  // clock keeps running no matter which part of the game is on screen.
  useIdleAutoAction()

  // `myGame` is populated asynchronously (socket response to
  // "game.visited-game-link"), so a missing game right after mount just means
  // it is still loading. Wait out a short grace period before deciding the
  // game is actually unavailable, to avoid flashing the not-available page.
  const [gaveUpWaiting, setGaveUpWaiting] = useState(false)

  // Keyboard shortcut for theme switcher
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "q") {
        event.preventDefault()
        dispatch(
          setModalData({
            themeSwitcher: { visible: true },
          })
        )
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [dispatch])

  useEffect(() => {
    if (!myGame) return
    if (activeAuctionPropertyOrder !== null) {
      dispatch(
        setModalData({
          buyOrAuction: { visible: false },
          auction: { visible: true },
        })
      )
    }
  }, [activeAuctionPropertyOrder, dispatch])
  useEffect(() => {
    socket.emit("game.visited-game-link", id as string)
  }, [])
  useEffect(() => {
    if (situation) {
      localStorage.setItem("state", JSON.stringify(situation))
    }
  }, [situation])
  useEffect(() => {
    return () => {
      dispatch(
        setGlobalElement({
          chanceCards: [],
          communityChestCards: [],
          jailOverlay: undefined,
          playerListItems: [],
        })
      )
    }
  }, [dispatch])

  useEffect(() => {
    if (myGame) {
      setGaveUpWaiting(false)
      return
    }
    const timeout = setTimeout(() => setGaveUpWaiting(true), 5000)
    return () => clearTimeout(timeout)
  }, [myGame])

  if (!myGame) {
    if (gaveUpWaiting) return <GameNotAvailable />

    // Still within the grace period — show a lightweight loader while the game
    // details are fetched.
    return (
      <div
        data-testid="game.loading"
        className="w-screen h-screen bg-cover bg-center flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${resolveBackgroundUrl(selectedThemeId)})`,
        }}
      >
        <div className="h-14 w-14 rounded-full border-4 border-white/30 border-t-white animate-spin" />
      </div>
    )
  }

  // Use the player's chosen background (persisted), falling back to the default.
  const backgroundUrl = resolveBackgroundUrl(selectedThemeId)

  const myPlayerOrder = getPlayerOrderFromPlayerId(myGame, getUserCreds().email)
  const showTradeModalForOtherPlayers = tradeData?.status === "accepted"

  return (
    <div
      date-testid="lobby"
      className={classNames(
        "w-screen h-screen bg-cover grid-cols-[1fr_auto_1fr] grid overflow-hidden select-none [-webkit-user-drag:none] [&_img]:select-none [&_img]:[-webkit-user-drag:none]",
        // The element stays the full screen so the background image does too —
        // the padding is what holds the three columns clear of a notch, a
        // punch-hole camera or the home indicator. Everything positioned
        // against a column (the settings cluster, the pace row) rides along
        // with it, since the columns are the positioned ancestors.
        "pt-[var(--safe-top)] pr-[var(--safe-right)] pb-[var(--safe-bottom)] pl-[var(--safe-left)]"
      )}
      style={{ backgroundImage: `url(${backgroundUrl})` }}
      onError={(e) => {
        // Fallback to default background if image fails to load
        e.currentTarget.style.backgroundImage = `url(${resolveBackgroundUrl()})`
      }}
      onDragStart={(e) => e.preventDefault()}
    >
      {modalData.bankrupt?.visible && <BankruptModal></BankruptModal>}
      {modalData.themeSwitcher?.visible && (
        <ThemeSwitcherModal></ThemeSwitcherModal>
      )}
      {modalData.settings?.visible && <SettingsModal></SettingsModal>}
      <HelpModal />
      <GuideModal />
      {modalData.leaveGameConfirm?.visible && (
        <LeaveGameConfirmModal></LeaveGameConfirmModal>
      )}
      {modalData.fastPaceNewPlayerConfirm?.visible && (
        <FastPaceNewPlayerConfirmModal></FastPaceNewPlayerConfirmModal>
      )}
      {isDemoMode && (
        <a
          href={ORIGINAL_GAME_STORE_LINK}
          target="_blank"
          rel="noreferrer"
          className="absolute bottom-[calc(1.5rem+var(--safe-bottom))] right-[calc(1.5rem+var(--safe-right))] z-[250] px-6 py-4 rounded-2xl text-lg font-bold tracking-wide text-white bg-blue-700 border-2 border-blue-400 shadow-xl hover:bg-blue-600 hover:scale-105 transition-all duration-200"
        >
          Unlock Full Version
        </a>
      )}
      {/* <navbar></navbar>

        
         <div className="grid grid-cols-[auto_min-content] gap-x-8"></div> */}
      {moneyAnimations.map((animation) => (
        <MoneyAnimation
          key={animation.id}
          animationData={{ ...animation }}
        ></MoneyAnimation>
      ))}
      <CenterNotifications></CenterNotifications>
      {/* The game's pace — the propose button, the vote panel and the fast-pace
          badge — lives at the head of the left player column rather than on a
          fixed layer over it, so it can claim real room there instead of
          hovering above the cards. See SidebarLeft. */}
      <HoveredTooltipItem></HoveredTooltipItem>

      <VoiceChat roomId={myGame?.id || ""}></VoiceChat>
      <SidebarLeft></SidebarLeft>
      <Board></Board>
      <Sidebar></Sidebar>
      {/* <div
        className={classNames(
          "flex flex-col fixed right-0 bottom-0 h-full z-[200] w-[300px] bg-white",
          {
            hidden: !state.showHistory,
          }
        )}
      >
        <GameHistory
          history={{
            gameid: state.myGame.id,
            history: state.myGame.situation?.historyItems || [],
            settings: state.myGame.settings,
            situation: state.myGame.situation as IGameSituation,
            users: state.myGame?.players || [],
          }}
        />

      </div> */}
      {showTradeModalForOtherPlayers && (
        <TradeNotification
          {...tradeData.activeTrade}
          eventType={tradeData.status}
          offeringPlayerOrder={myPlayerOrder}
          offeredPlayerOrder={
            tradeData.activeTrade?.offeredPlayerOrder || 0
          }
          givenProperties={tradeData.activeTrade?.givenProperties || []}
          takenProperties={tradeData.activeTrade?.takenProperties || []}
          moneyGiven={tradeData.activeTrade?.moneyGiven || 0}
          moneyTaken={tradeData.activeTrade?.moneyTaken || 0}
        ></TradeNotification>
      )}
      {ActiveModal && <ActiveModal></ActiveModal>}
      {modalData.buyOrAuction?.visible && (
        <BuyOrAuctionModal></BuyOrAuctionModal>
      )}
      <SayNoModal></SayNoModal>

      {modalData.playerCards?.visible && (
        <PlayerCardsModal></PlayerCardsModal>
      )}
      {modalData.jailFreeCard?.visible && (
        <JailFreeCardModal></JailFreeCardModal>
      )}
      {modalData.stockExchange?.visible && <StockModal></StockModal>}
      {modalData.decisionHub?.visible && (
        <DecisionHubModal></DecisionHubModal>
      )}
      <DoubleRentModalPage />
      <ForcedBuyModalPage />

      <EndGameModalPage></EndGameModalPage>
      {modalData.auction?.visible && <AuctionPage></AuctionPage>}

      <TradePlayerChooser></TradePlayerChooser>
      {(modalData.trade?.visible || viewedTradeId) && (
        <CreateTradeModal />
      )}
      {modalData.propMng?.visible && (
        <PropertyManagementPage></PropertyManagementPage>
      )}
      {modalData.rolePickingModal?.visible && (
        <RolePickingModalPage></RolePickingModalPage>
      )}
      {modalData.auctionResult?.visible && <AuctionResult />}
      {situation && <HiddenAuctionModal />}
      <HouseBuildModal />
      {modalData.fullStats?.visible && (
        <FullStats
          gameId={myGame?.id || ""}
          onClose={() => {
            dispatch(setModalData({ fullStats: { visible: false } }))
          }}
        />
      )}

      {featureFlags.showChat && <GlobalChat />}
      <PauseOverlay />
    </div>
  )
}

export default Game
